import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { API_AUTH_USER, VIEW_LOGIN } from '../../common/EsConstants';
import { esFetch } from '../../common/EsFetch';
import { esSessionIsLeaving, esSessionOnRefused, esSessionTouchedAt } from '../../common/EsSessionClock';
import { useAuth } from './EsAuth';

// Past the idle limit before asking, so the check never lands on a session with a second to go.
export const EXPIRY_MARGIN_MS = 5000;
// A browser runs a setTimeout past 2^31 - 1 ms (24.8 days) at once. A kept session idles for 30
// days (T99), so a long wait is cut to this and the timer re-arms for what is left.
export const LONGEST_TIMER_MS = 2147483647;

/**
 * Sends a signed-in reader to the login when their session runs out, and back to this page after
 * (Tim, T9). Effect-only, in the shell, because AuthProvider sits above the router and cannot
 * navigate.
 *
 * <p><b>It asks only once the session can have expired.</b> Any request restarts the session's
 * idle clock, so a poll of authUser would keep it alive forever. The timer is set for the last
 * answer esFetch saw plus esback's idle limit; when it fires, one authUser says whether the
 * session is gone. Still there (another tab, or a request esFetch did not carry, kept it) and
 * the timer starts again from now.
 *
 * <p>A timer is late after a sleep, so a tab coming back into view checks at once if it is due,
 * and a 401 from any esFetch checks straight away.
 */
export default function EsSessionExpiry() {
    const { user, sessionSeconds, expire } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Read when the session goes, not a reason to set the timer again on every route change.
    const here = useRef( location );
    here.current = location;

    useEffect( () => {
        if ( !user || !sessionSeconds ) return undefined;

        let aLive = true;
        let aTimer = null;
        let aAsking = false;
        const aLimit = sessionSeconds * 1000 + EXPIRY_MARGIN_MS;
        const due = () => esSessionTouchedAt() + aLimit - Date.now();

        const gone = () => {
            void navigate( VIEW_LOGIN, { replace: true, state: { from: here.current, expired: true } } );
            expire();
        };

        const ask = async () => {
            if ( aAsking || esSessionIsLeaving() ) return;
            aAsking = true;
            let aBody = null;
            try {
                const aResponse = await esFetch( API_AUTH_USER );
                if ( aResponse.ok ) aBody = await aResponse.text();
            }
            catch {
                // esback unreachable, or a gateway answering for it, says nothing about the session.
            }
            aAsking = false;
            if ( !aLive || esSessionIsLeaving() ) return;
            if ( aBody === '' ) gone();
            // Asked again a whole limit from now when esback did not answer, since nothing
            // touched the clock and the next due() would be at once.
            else arm( aBody === null ? aLimit : due() );
        };

        const arm = ( pWait ) => {
            clearTimeout( aTimer );
            aTimer = setTimeout( () => ( due() > 0 ? arm( due() ) : ask() ), Math.min( Math.max( pWait, 0 ), LONGEST_TIMER_MS ) );
        };

        const onVisible = () => {
            if ( document.visibilityState === 'visible' && due() <= 0 ) void ask();
        };

        arm( due() );
        document.addEventListener( 'visibilitychange', onVisible );
        const aOff = esSessionOnRefused( () => void ask() );

        return () => {
            aLive = false;
            clearTimeout( aTimer );
            document.removeEventListener( 'visibilitychange', onVisible );
            aOff();
        };
    }, [ user, sessionSeconds, expire, navigate ] );

    return null;
}
